import React, { useState, useRef, useEffect, memo } from "react";
import { IconColumns, IconCheck } from "@tabler/icons-react";

export interface ColumnConfig {
  key: string;
  label: string;
  visible: boolean;
  required?: boolean;
}

interface ColumnVisibilityDropdownProps {
  columns: ColumnConfig[];
  onChange: (key: string, visible: boolean) => void;
  onReset?: () => void;
}

const ColumnVisibilityDropdown: React.FC<ColumnVisibilityDropdownProps> = ({
  columns,
  onChange,
  onReset,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const visibleCount = columns.filter((col) => col.visible).length;

  return (
    <div className="position-relative" ref={containerRef}>
      <button
        type="button"
        className="btn btn-outline-secondary d-flex align-items-center gap-1"
        onClick={() => setOpen((prev) => !prev)}
        title="Tuỳ chỉnh hiển thị cột"
      >
        <IconColumns size={16} />
        <span>Ẩn/Hiện cột ({visibleCount}/{columns.length})</span>
      </button>

      {open && (
        <div
          className="dropdown-menu show position-absolute end-0 mt-1 shadow border p-2"
          style={{ width: "230px", zIndex: 1050 }}
        >
          <div className="d-flex align-items-center justify-content-between mb-2 pb-1 border-bottom px-1">
            <span className="fw-bold small text-uppercase">Cột hiển thị</span>
            {onReset && (
              <button
                type="button"
                className="btn btn-link btn-sm p-0 text-decoration-none small text-muted"
                onClick={onReset}
              >
                Mặc định
              </button>
            )}
          </div>

          <div className="d-flex flex-column gap-1" style={{ maxHeight: "260px", overflowY: "auto" }}>
            {columns.map((col) => (
              <label
                key={col.key}
                className={`dropdown-item d-flex align-items-center justify-content-between rounded px-2 py-1 cursor-pointer ${
                  col.required ? "opacity-75" : ""
                }`}
              >
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="checkbox"
                    className="form-check-input mt-0"
                    checked={col.visible}
                    disabled={col.required}
                    onChange={(e) => onChange(col.key, e.target.checked)}
                  />
                  <span>{col.label}</span>
                </div>
                {col.visible && <IconCheck size={14} className="text-reset" />}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(ColumnVisibilityDropdown);
